/**
 * activityUtils — W1.8
 *
 * Utility-Funktionen fuer den Activity-Feed.
 * Ausgelagert aus ActivityListItem damit react-refresh keine Warnung wirft.
 * buildCsvContent + triggerCsvDownload ausgelagert aus ActivityFeedPage
 * damit react-refresh keine Warnung wirft (nicht-Komponenten-Exports).
 */
import { formatDistanceToNow, format, differenceInHours } from 'date-fns'
import { activeDateFnsLocale, isEnLocale } from '../../lib/locale'
import { CATEGORY_KINDS } from './constants/actionTypeConfig'
import type { ActivityGroup } from './api/activityApi'

// --- KPI-Berechnung ---

export type KpiData = {
  rewards30d: number
  txCount: number
  netInflow: number
}

export function calculateKpis(items: ActivityGroup[]): KpiData {
  const rewardKinds = new Set(CATEGORY_KINDS.rewards)
  const rewards30d = items
    .filter((i) => rewardKinds.has(i.kind))
    .reduce((sum, i) => sum + i.rewards_total, 0)
  const txCount = items.reduce((sum, i) => sum + i.activity_count, 0)
  // Netto-Zufluss: nur echte Reward-Summe — Kommissionen und Gebühren fehlen in dieser API,
  // daher ist netInflow hier identisch mit rewards30d (kein Fantasie-Abzug).
  const netInflow = rewards30d

  return {
    rewards30d,
    txCount,
    netInflow,
  }
}

// --- CSV-Export-Hilfsfunktionen ---

/** Escaped ein einzelnes CSV-Feld (umschließt mit Anführungszeichen wenn nötig). */
function escapeCsvField(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return ''
  const str = String(value)
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`
  }
  return str
}

/** Erstellt CSV-Inhalt aus einer Liste von ActivityGroup-Objekten. */
export function buildCsvContent(items: ActivityGroup[]): string {
  const header = ['Datum', 'Typ', 'Hotspots', 'Token', 'Betrag', 'Anzahl'].join(',')
  const rows = items.map((item) => {
    const datum = escapeCsvField(item.occurred_at)
    const typ = escapeCsvField(item.human_label)
    const hotspots = escapeCsvField(item.hotspots.map((h) => h.name ?? '').join('; '))
    const token = escapeCsvField(item.token ?? '')
    const betrag = escapeCsvField(item.rewards_total)
    const anzahl = escapeCsvField(item.activity_count)
    return [datum, typ, hotspots, token, betrag, anzahl].join(',')
  })
  return [header, ...rows].join('\n')
}

/** Triggert einen Browser-Download für den gegebenen Inhalt. */
export function triggerCsvDownload(content: string, filename: string): void {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

// --- Datums-Formatierung ---

export function formatActivityDate(occurredAt: string): { display: string; tooltip: string } {
  const date = new Date(occurredAt)
  const loc = activeDateFnsLocale()
  const en = isEnLocale()
  const tooltip = format(date, en ? 'MMMM d, yyyy, h:mm a' : "d. MMMM yyyy, HH:mm 'Uhr'", { locale: loc })

  const hoursAgo = differenceInHours(new Date(), date)
  if (hoursAgo < 24) {
    const display = formatDistanceToNow(date, { addSuffix: true, locale: loc })
    return { display, tooltip }
  }

  const display = format(date, en ? 'MMM d, yyyy, h:mm a' : "d. MMM yyyy, HH:mm 'Uhr'", { locale: loc })
  return { display, tooltip }
}
